/**
 * realtimeBus — the ONE Socket.io connection owner for the whole web app.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * Before this module there were SEVEN independently-owned socket owners: the
 * Feathers `apiClient` socket plus six hand-rolled copies of the same
 * "module-level shared socket + Set<handler> + refcount + teardownIfIdle"
 * block (`stores/RealtimeStore`, `views/clients/clientsRealtime`,
 * `views/clients/ClientList/clientsLive`, `views/devops/devopsLiveStatus`,
 * `views/meetingIntelligence/meetingIntelligenceRealtime`,
 * `views/home/homeLive`). Their own comments admitted the copying. Two things
 * went wrong as a direct result:
 *
 *  1. SILENT DEAD SUBSCRIPTIONS. Every copy took the JWT as a caller-supplied
 *     parameter and every copy began `if (!token) return;` — so a consumer that
 *     forgot the argument got a subscription that registered a handler, tore
 *     down cleanly, logged nothing, and received nothing, forever. Three
 *     Meeting-Intelligence stores and the Command Center shipped that way.
 *  2. SELF-DoS. The API rate-limits sockets at 20 connections/min/IP. A user on
 *     Client 360 could hold four concurrent websockets; ordinary cross-module
 *     navigation tripped the limit and locked the app into silent timeouts.
 *
 * ---------------------------------------------------------------------------
 * ARCHITECTURE
 * ---------------------------------------------------------------------------
 *   consumers ──subscribeRealtime(rootStore, channel, handler)──┐
 *                                                               │
 *   channel = { name, wireEvents[], translate() }  ← ONE translator per SERVER
 *                                                    event type, never per
 *                                                    consumer. Leaner consumers
 *                                                    SELECT from the richer
 *                                                    canonical shape.
 *                                                               │
 *                                                               ▼
 *                             realtimeBus  ── refcount (one place) ──► connection
 *
 * CONNECTION SOURCE (in priority order, resolved here and nowhere else):
 *   1. `rootStore.app.io` — the raw socket underneath the Feathers client. It
 *      is already open, already JWT-authenticated (apiClient puts the token in
 *      the handshake `query.Authorization`, and the API's socket middleware in
 *      `app.ts` authenticates from it), and already joined to the `internal`
 *      staff channel, which is where every event this bus cares about is
 *      published. Reusing it costs ZERO extra connections. The bus never
 *      disconnects a socket it does not own.
 *   2. A bus-owned dedicated socket, built from the AuthStore JWT, used only
 *      when the Feathers client has no transport (e.g. `mode: "test"`). Owned,
 *      refcounted, and disconnected when the last lease is released.
 *
 * AUTH IS INTERNAL, BY CONSTRUCTION. No exported function on this module takes
 * a token. The bus reads `rootStore.authStore?.token` itself. It is therefore
 * IMPOSSIBLE for a caller to subscribe "without auth" and get silence — the
 * caller has no way to express that state. If a subscription cannot be
 * established the bus records a LOUD problem (see {@link realtimeDiagnostics})
 * and `console.error`s it; it never fails as a no-op.
 *
 * TEARDOWN CANNOT LEAK. Wire listeners are attached when a channel gains its
 * first subscriber and removed when it loses its last, by the same code path.
 * A channel's listeners are stored in a Map keyed by wire-event name so
 * `socket.off(name, fn)` always removes exactly what `socket.on(name, fn)`
 * added (the old copies used `removeAllListeners()`, which would have ripped
 * out the Feathers client's own listeners had they shared a socket).
 *
 * RECONNECTION — see the block above {@link handleConnect}.
 *
 * FRESHNESS POLICY — see `realtime/freshnessPolicy.ts`.
 */
import { makeAutoObservable, reaction, runInAction } from "mobx";
import type { Socket } from "socket.io-client";
import type RootStore from "stores/RootStore";

// ---------------------------------------------------------------------------
// Public channel contract
// ---------------------------------------------------------------------------

/**
 * A named, translated slice of the realtime stream.
 *
 * `translate` is the ONLY place a server payload is parsed. It returns the
 * canonical (richest) domain shape for that server event type, or `null` to
 * drop the payload entirely. Consumers that need less than the canonical shape
 * select from it in their own thin wrapper — they do NOT get a second parser.
 */
export interface RealtimeChannel<T> {
  /** Stable diagnostic name. Must also appear in `freshnessPolicy.ts`. */
  readonly name: string;
  /** Socket.io wire event names this channel listens on. */
  readonly wireEvents: readonly string[];
  /** Parse one wire payload into the canonical shape (or drop it). */
  translate(wireEvent: string, raw: unknown): T | null;
}

/** A held claim on the shared connection. `release()` is idempotent. */
export interface RealtimeLease {
  /** The shared socket, or null when no connection could be established. */
  readonly socket: Socket | null;
  release(): void;
}

export type RealtimeSource = "feathers" | "dedicated" | null;
export type RealtimeStatus =
  /** No subscriber has ever asked for a connection. */
  | "idle"
  /** A connection is resolved and channels are attached. */
  | "attached"
  /** Subscribers exist but no socket could be built — LOUD failure. */
  | "unavailable";

export interface RealtimeAreaDiagnostic {
  /** Live subscriber count for this channel. */
  subscribers: number;
  /** True when the channel's wire listeners are bound to the shared socket. */
  attached: boolean;
  /** Epoch ms of the last payload this channel translated and dispatched. */
  lastEventAt: number | null;
  /** Payloads received but dropped by the translator (unknown type, etc.). */
  dropped: number;
  /** Last reason this channel could not be established, if any. */
  lastProblem: string | null;
}

export interface RealtimeProblem {
  area: string;
  message: string;
  at: number;
}

/**
 * Observable state of the bus. This is the answer to "a subscription that
 * cannot be established must be observable, never a no-op": every failure
 * lands here AND on `console.error`.
 *
 * Also mirrored to `window.__intrasyncRealtime` so it can be read from the
 * browser console / a Playwright run without wiring a UI.
 */
class RealtimeDiagnostics {
  status: RealtimeStatus = "idle";
  source: RealtimeSource = null;
  connected = false;
  /** Number of outstanding leases on the shared connection. */
  leases = 0;
  /** How many sockets this bus has OPENED itself (0 on the Feathers path). */
  socketsOpened = 0;
  areas: Record<string, RealtimeAreaDiagnostic> = {};
  problems: RealtimeProblem[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  area(name: string): RealtimeAreaDiagnostic {
    if (!this.areas[name]) {
      this.areas[name] = {
        subscribers: 0,
        attached: false,
        lastEventAt: null,
        dropped: 0,
        lastProblem: null,
      };
    }
    return this.areas[name];
  }
}

export const realtimeDiagnostics = new RealtimeDiagnostics();

/** Max problems retained (newest last) so a flapping socket can't grow forever. */
const MAX_PROBLEMS = 25;

function recordProblem(area: string, message: string): void {
  runInAction(() => {
    realtimeDiagnostics.area(area).lastProblem = message;
    realtimeDiagnostics.problems.push({ area, message, at: Date.now() });
    if (realtimeDiagnostics.problems.length > MAX_PROBLEMS) {
      realtimeDiagnostics.problems.shift();
    }
  });
  // LOUD by design — the class of bug this bus exists to kill was silent.
  console.error(`[realtime:${area}] ${message}`);
}

function publishDiagnosticsHandle(): void {
  if (typeof window === "undefined") return;
  (window as unknown as { __intrasyncRealtime?: unknown }).__intrasyncRealtime =
    realtimeDiagnostics;
}

// ---------------------------------------------------------------------------
// Connection ownership (the ONE refcount)
// ---------------------------------------------------------------------------

interface Connection {
  socket: Socket;
  /** True when the bus created this socket and must disconnect it on release. */
  owned: boolean;
  /** The JWT an owned socket was built with (used to detect a token refresh). */
  token: string | null;
}

let connection: Connection | null = null;
let resolving: Promise<Socket | null> | null = null;
let leaseCount = 0;
let lifecycleBound: (() => void) | null = null;
let tokenWatcher: (() => void) | null = null;

interface ChannelState {
  channel: RealtimeChannel<unknown>;
  handlers: Set<(event: unknown) => void>;
  /** wire event name -> the exact listener registered for it. */
  wired: Map<string, (raw: unknown) => void>;
  lease: RealtimeLease | null;
  leasePending: boolean;
}

const channels = new Map<string, ChannelState>();

function feathersSocket(rootStore: RootStore | null | undefined): Socket | null {
  // `app.io` is the raw socket.io Socket that @feathersjs/socketio-client hangs
  // off the application (see its index.d.ts module augmentation). Read
  // structurally so this module does not depend on that augmentation being
  // loaded.
  const app = (rootStore as unknown as { app?: { io?: Socket | null } } | null)
    ?.app;
  const socket = app?.io ?? null;
  return socket ?? null;
}

function authToken(rootStore: RootStore | null | undefined): string | null {
  return (
    (rootStore as unknown as { authStore?: { token?: string | null } } | null)
      ?.authStore?.token ?? null
  );
}

/**
 * RECONNECTION, IN ONE PLACE.
 *
 * Feathers path (the normal one): socket.io reconnects the SAME Socket object,
 * so every listener this bus attached survives the drop — there is nothing to
 * re-attach. Auth is re-presented from the handshake, and `apiClient.ts`
 * refreshes an expired JWT on `reconnect_attempt` before the handshake is
 * rebuilt, so the reconnected socket rejoins the `internal` channel. The bus's
 * only job is to keep `connected` honest for the UI.
 *
 * Dedicated path (no Feathers transport): the bus watches the AuthStore token.
 * A token change means the handshake this socket was built with is stale, so
 * the socket is rebuilt and every channel re-attached — see
 * {@link revalidateToken}. That is why teardown is centralised: rebinding N
 * channels to a new socket is one loop here instead of six copies elsewhere.
 */
function handleConnect(): void {
  runInAction(() => {
    realtimeDiagnostics.connected = true;
  });
}

function handleDisconnect(): void {
  runInAction(() => {
    realtimeDiagnostics.connected = false;
  });
}

function bindLifecycle(socket: Socket): void {
  if (lifecycleBound) return;
  socket.on("connect", handleConnect);
  socket.on("disconnect", handleDisconnect);
  lifecycleBound = () => {
    socket.off("connect", handleConnect);
    socket.off("disconnect", handleDisconnect);
  };
  runInAction(() => {
    realtimeDiagnostics.connected = !!socket.connected;
  });
}

function unbindLifecycle(): void {
  lifecycleBound?.();
  lifecycleBound = null;
}

/** Build the bus-owned fallback socket. Returns null (loudly) with no token. */
async function openDedicatedSocket(
  rootStore: RootStore,
  area: string
): Promise<Connection | null> {
  const token = authToken(rootStore);
  if (!token) {
    recordProblem(
      area,
      "no realtime connection: the Feathers client exposes no socket and the " +
        "AuthStore has no access token, so no authenticated socket can be " +
        "built. Events for this area will not arrive until the user is signed " +
        "in; the bus retries on the next subscribe."
    );
    runInAction(() => {
      realtimeDiagnostics.status = "unavailable";
    });
    return null;
  }
  const [{ io }, { env }] = await Promise.all([
    import("socket.io-client"),
    import("../config/environment"),
  ]);
  const socket = io(env.apiUrl, {
    transports: ["websocket", "polling"],
    auth: { accessToken: token, strategy: "jwt" },
    // Also passed as a query param for the legacy middleware on the API.
    query: {
      Authorization: encodeURIComponent(
        JSON.stringify({ strategy: "jwt", accessToken: token })
      ),
    },
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    timeout: 20000,
  });
  socket.on("connect_error", (err: { message?: string }) => {
    recordProblem(area, `socket connect_error: ${err?.message ?? "unknown"}`);
  });
  runInAction(() => {
    realtimeDiagnostics.socketsOpened += 1;
  });
  return { socket, owned: true, token };
}

async function resolveConnection(
  rootStore: RootStore,
  area: string
): Promise<Socket | null> {
  if (connection) return connection.socket;
  if (resolving) return resolving;

  resolving = (async (): Promise<Socket | null> => {
    const shared = feathersSocket(rootStore);
    if (shared) {
      connection = { socket: shared, owned: false, token: authToken(rootStore) };
    } else {
      connection = await openDedicatedSocket(rootStore, area);
    }
    if (!connection) return null;
    bindLifecycle(connection.socket);
    if (connection.owned) watchTokenForOwnedSocket(rootStore);
    runInAction(() => {
      realtimeDiagnostics.source = connection!.owned ? "dedicated" : "feathers";
      realtimeDiagnostics.status = "attached";
    });
    publishDiagnosticsHandle();
    // A channel may have been registered while this promise was in flight.
    attachAllChannels();
    return connection.socket;
  })();

  try {
    return await resolving;
  } finally {
    resolving = null;
  }
}

/**
 * Acquire a lease on the shared connection. Every consumer path — translated
 * channels AND the raw-socket streams in `views/infrastructure/instanceStreams`
 * — goes through here, so the refcount lives in exactly one place.
 */
export async function acquireRealtimeSocket(
  rootStore: RootStore,
  area: string
): Promise<RealtimeLease> {
  leaseCount += 1;
  runInAction(() => {
    realtimeDiagnostics.leases = leaseCount;
    realtimeDiagnostics.area(area);
  });
  const socket = await resolveConnection(rootStore, area);
  let released = false;
  return {
    socket,
    release() {
      if (released) return;
      released = true;
      leaseCount = Math.max(0, leaseCount - 1);
      runInAction(() => {
        realtimeDiagnostics.leases = leaseCount;
      });
      if (leaseCount === 0) teardownConnection();
    },
  };
}

/**
 * Watch the AuthStore JWT for a bus-OWNED socket, so a token refresh rebuilds
 * the handshake in ONE place instead of leaving a socket authenticated with a
 * dead token. No-op on the Feathers path — `apiClient.ts` already refreshes the
 * token on `reconnect_attempt` there, and adding a second refresher would be
 * exactly the duplication this module exists to remove.
 */
function watchTokenForOwnedSocket(rootStore: RootStore): void {
  if (tokenWatcher) return;
  tokenWatcher = reaction(
    () => authToken(rootStore),
    () => {
      void revalidateToken(rootStore);
    }
  );
}

/** Drop the connection when the last lease goes. Owned sockets get closed. */
function teardownConnection(): void {
  if (!connection) return;
  const { socket, owned } = connection;
  tokenWatcher?.();
  tokenWatcher = null;
  unbindLifecycle();
  connection = null;
  runInAction(() => {
    realtimeDiagnostics.source = null;
    realtimeDiagnostics.status = "idle";
    realtimeDiagnostics.connected = false;
  });
  if (owned) socket.disconnect();
}

/**
 * Rebuild a bus-OWNED socket when the JWT changes (token refresh / re-login).
 * A no-op on the Feathers path, where `apiClient` owns refresh. Safe to call
 * from anywhere; it re-attaches every live channel to the new socket.
 */
export async function revalidateToken(rootStore: RootStore): Promise<void> {
  if (!connection || !connection.owned) return;
  const token = authToken(rootStore);
  if (token === connection.token) return;
  const live = [...channels.values()].filter((st) => st.handlers.size > 0);
  for (const st of live) detachWires(st);
  const old = connection;
  unbindLifecycle();
  connection = null;
  old.socket.disconnect();
  const next = await openDedicatedSocket(rootStore, "token-refresh");
  if (!next) return;
  connection = next;
  bindLifecycle(next.socket);
  attachAllChannels();
}

// ---------------------------------------------------------------------------
// Channels
// ---------------------------------------------------------------------------

function ensureChannelState(channel: RealtimeChannel<unknown>): ChannelState {
  const existing = channels.get(channel.name);
  if (existing) {
    if (existing.channel !== channel) {
      // Two different channel objects claiming one name would silently share a
      // handler set and one translator. Loud, not silent.
      recordProblem(
        channel.name,
        "two different RealtimeChannel objects registered under the same name; " +
          "the first registration wins and the second's translator is ignored."
      );
    }
    return existing;
  }
  const state: ChannelState = {
    channel,
    handlers: new Set(),
    wired: new Map(),
    lease: null,
    leasePending: false,
  };
  channels.set(channel.name, state);
  return state;
}

function dispatch(state: ChannelState, wireEvent: string, raw: unknown): void {
  let event: unknown;
  try {
    event = state.channel.translate(wireEvent, raw);
  } catch (err) {
    recordProblem(
      state.channel.name,
      `translator threw on "${wireEvent}": ${String(err)}`
    );
    return;
  }
  if (event === null || event === undefined) {
    runInAction(() => {
      realtimeDiagnostics.area(state.channel.name).dropped += 1;
    });
    return;
  }
  runInAction(() => {
    realtimeDiagnostics.area(state.channel.name).lastEventAt = Date.now();
  });
  // Snapshot: a handler may unsubscribe (or subscribe) during dispatch.
  for (const handler of [...state.handlers]) {
    try {
      handler(event);
    } catch (err) {
      // One bad subscriber must never starve the others, and must never throw
      // out of a socket callback.
      console.error(`[realtime:${state.channel.name}] handler error:`, err);
    }
  }
}

function attachWires(state: ChannelState): void {
  if (!connection || state.wired.size > 0 || state.handlers.size === 0) return;
  for (const wireEvent of state.channel.wireEvents) {
    const listener = (raw: unknown) => dispatch(state, wireEvent, raw);
    state.wired.set(wireEvent, listener);
    connection.socket.on(wireEvent, listener);
  }
  runInAction(() => {
    realtimeDiagnostics.area(state.channel.name).attached = true;
  });
}

function detachWires(state: ChannelState): void {
  if (state.wired.size === 0) return;
  // Note: `off(name, fn)` — NEVER removeAllListeners(). The shared socket also
  // carries the Feathers client's own listeners.
  if (connection) {
    for (const [wireEvent, listener] of state.wired) {
      connection.socket.off(wireEvent, listener);
    }
  }
  state.wired.clear();
  runInAction(() => {
    realtimeDiagnostics.area(state.channel.name).attached = false;
  });
}

function attachAllChannels(): void {
  for (const state of channels.values()) {
    if (state.handlers.size > 0) attachWires(state);
  }
}

/**
 * Subscribe to a channel. Returns an idempotent disposer.
 *
 * Refcounting: the FIRST subscriber to a channel takes a lease on the shared
 * connection and binds the channel's wire listeners; the LAST one to leave
 * unbinds them and releases the lease. The connection itself only goes away
 * when the last lease across ALL channels is released.
 *
 * There is deliberately no `token` parameter — see the module header.
 */
export function subscribeRealtime<T>(
  rootStore: RootStore,
  channel: RealtimeChannel<T>,
  handler: (event: T) => void
): () => void {
  const state = ensureChannelState(channel as RealtimeChannel<unknown>);
  state.handlers.add(handler as (event: unknown) => void);
  runInAction(() => {
    realtimeDiagnostics.area(channel.name).subscribers = state.handlers.size;
  });

  // Fast path: a connection is already up, so bind immediately (synchronously)
  // — no await, no window in which an event could be missed.
  if (connection) attachWires(state);

  if (!state.lease && !state.leasePending) {
    state.leasePending = true;
    void acquireRealtimeSocket(rootStore, channel.name)
      .then((lease) => {
        state.leasePending = false;
        if (state.handlers.size === 0) {
          // Everyone left while we were resolving — do not hold the connection.
          lease.release();
          return;
        }
        state.lease = lease;
        if (!lease.socket) {
          recordProblem(
            channel.name,
            "subscription registered but no socket is available; this area is " +
              "NOT receiving live events. It will bind as soon as a connection " +
              "resolves, and retries on the next subscribe."
          );
          return;
        }
        attachWires(state);
      })
      .catch((err) => {
        state.leasePending = false;
        recordProblem(channel.name, `failed to acquire connection: ${String(err)}`);
      });
  } else if (!connection && !resolving) {
    // We already hold a lease but the previous resolution produced no socket.
    // Every fresh subscribe is a retry opportunity — this is what makes a
    // token-arrives-later start-up self-heal instead of staying silently dead.
    void resolveConnection(rootStore, channel.name).then(() => attachAllChannels());
  }

  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    state.handlers.delete(handler as (event: unknown) => void);
    runInAction(() => {
      realtimeDiagnostics.area(channel.name).subscribers = state.handlers.size;
    });
    if (state.handlers.size > 0) return;
    detachWires(state);
    const lease = state.lease;
    state.lease = null;
    lease?.release();
  };
}

/** True when the shared connection exists and is currently connected. */
export function isRealtimeConnected(rootStore: RootStore): boolean {
  if (connection) return !!connection.socket.connected;
  return !!feathersSocket(rootStore)?.connected;
}

/**
 * The shared socket if one is already resolved, else the Feathers socket if the
 * app has one. Read-only peek — takes no lease, never opens a connection.
 */
export function peekRealtimeSocket(rootStore: RootStore): Socket | null {
  return connection?.socket ?? feathersSocket(rootStore);
}

/** Test-only: drop all state so each test starts from a clean bus. */
export function resetRealtimeBusForTests(): void {
  for (const state of channels.values()) {
    detachWires(state);
    state.handlers.clear();
    state.lease = null;
    state.leasePending = false;
  }
  channels.clear();
  if (connection?.owned) connection.socket.disconnect();
  tokenWatcher?.();
  tokenWatcher = null;
  unbindLifecycle();
  connection = null;
  resolving = null;
  leaseCount = 0;
  runInAction(() => {
    realtimeDiagnostics.status = "idle";
    realtimeDiagnostics.source = null;
    realtimeDiagnostics.connected = false;
    realtimeDiagnostics.leases = 0;
    realtimeDiagnostics.socketsOpened = 0;
    realtimeDiagnostics.areas = {};
    realtimeDiagnostics.problems = [];
  });
}
